import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const contactMethods = [
  {
    icon: 'Phone',
    label: 'Appelez-nous Aujourd\'hui',
    description: 'Parlez directement avec notre équipe',
    value: '+243 81 888 3531',
    href: 'tel:+243818883531',
    isLink: true,
  },
  {
    icon: 'Mail',
    label: 'Envoyez-nous un Email',
    description: 'Obtenez des informations détaillées',
    value: 'info@orazek.com',
    href: 'mailto:info@orazek.com',
    isLink: true,
  },
  {
    icon: 'MapPin',
    label: 'Visitez Notre Bureau',
    description: 'Rencontrez-nous en personne',
    value: 'Kinshasa, DRC',
    href: null,
    isLink: false,
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

const CTASection = () => {
  return (
    <section className="py-20 lg:py-28 bg-slate-100 dark:bg-slate-900 relative overflow-hidden">
      {/* Animated blob backgrounds */}
      <motion.div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-3xl"
        animate={{
          x: [0, 30, -20, 0],
          y: [0, -20, 30, 0],
          scale: [1, 1.1, 0.95, 1],
        }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-amber-500/8 blur-3xl"
        animate={{
          x: [0, -30, 20, 0],
          y: [0, 20, -30, 0],
          scale: [1, 0.95, 1.1, 1],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-slate-700/20 blur-3xl"
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.2, 0.35, 0.2],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
        }}
      />

      {/* Radial gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-0 dark:opacity-80" />

      {/* Content */}
      <motion.div
        className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
      >
        {/* Title area */}
        <motion.div className="text-center mb-12 md:mb-16" variants={itemVariants}>
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-5">
            <span className="bg-gradient-to-r from-blue-400 to-amber-400 bg-clip-text text-transparent">
              Prêt à Vivre l'Excellence ORAZEK?
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Rejoignez plus de 500 clients satisfaits qui font confiance à ORAZEK Investment pour des solutions d'affaires complètes et fiables à travers la République Démocratique du Congo.
          </p>
        </motion.div>

        {/* Contact method cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12 md:mb-16"
          variants={containerVariants}
        >
          {contactMethods.map((method, index) => (
            <motion.div
              key={index}
              className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl p-8 shadow-2xl cursor-pointer group hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:border-blue-500/30 transition-all duration-300"
              variants={itemVariants}
              whileHover={{ scale: 1.03, y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="flex flex-col items-center text-center">
                <motion.div
                  className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg flex items-center justify-center mb-5"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <Icon name={method.icon} size={28} className="text-white" />
                </motion.div>

                <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-3">
                  {method.label}
                </h3>

                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                  {method.description}
                </p>

                {method.isLink ? (
                  <a
                    href={method.href}
                    className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white group-hover:text-amber-400 transition-colors duration-300"
                  >
                    {method.value}
                  </a>
                ) : (
                  <span className="text-xl md:text-2xl font-bold text-amber-400">
                    {method.value}
                  </span>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          variants={itemVariants}
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Link to="/contact">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/25 border-none w-full sm:w-auto rounded-xl px-8 py-3"
              >
                Réserver une Consultation
              </Button>
            </Link>
          </motion.div>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
            <Link to="/services-overview">
              <Button
                variant="outline"
                size="lg"
                iconName="Grid"
                iconPosition="left"
                className="border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 backdrop-blur-sm w-full sm:w-auto rounded-xl px-8 py-3"
              >
                Explorer les Services
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default CTASection;
